'use client';
import { useCallback, useState } from 'react';
import { Calendar as CalendarBlock } from 'calendar-blocks';
import Month from './Month';
import { cn } from '@/lib/utils';

export type CalendarProps = {
  confirmedDates: { start: Date | null; end: Date | null };
  rangeValue: { start: Date | null; end: Date | null };
  onRangeValueChange: (value: { start: Date | null; end: Date | null }) => void;
  lang: string;
  isOneDate?: boolean;
};

const now = new Date();

export default function Calendar({
  confirmedDates,
  rangeValue,
  onRangeValueChange,
  lang,
  isOneDate = false,
}: CalendarProps) {
  const [viewInfo, setViewInfo] = useState<{
    month: number;
    year: number;
  }>({
    month: confirmedDates?.end ? confirmedDates.end.getMonth() : now.getMonth(),
    year: confirmedDates?.end
      ? confirmedDates.end.getFullYear()
      : now.getFullYear(),
  });

  const getDateEnabled = useCallback(
    (date: Date) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const currentDate = new Date(date);
      currentDate.setHours(0, 0, 0, 0);

      if (rangeValue.start && !rangeValue.end) {
        return currentDate >= rangeValue.start;
      }

      return currentDate >= today;
    },
    //eslint-disable-next-line
    [rangeValue.start, rangeValue.end],
  );

  function handleOnRangeChange(dates: {
    start: Date | null;
    end: Date | null;
  }) {
    onRangeValueChange(dates);
  }

  const onMonthChange = useCallback(
    ({ month: newMonth, year: newYear }: { month: number; year: number }) => {
      if (newMonth === viewInfo.month + 1 && newYear === viewInfo.year) {
        return;
      }

      setViewInfo({
        month: newMonth,
        year: newYear,
      });
    },
    [setViewInfo, viewInfo.month, viewInfo.year],
  );

  return (
    <div
      className={cn(
        'w-[290px] desktop:max-w-[800px] flex flex-col my-[20px] mx-auto desktop:w-full min-h-[360px]',
      )}>
      <CalendarBlock
        className={cn(
          'flex flex-col items-center desktop:flex-row desktop:justify-around [@media(min-width:716px)]:gap-[3rem] mb-[20px] desktop:items-start desktop:h-[350px] desktop:mb-0',
        )}
        displayMonth={viewInfo.month}
        displayYear={viewInfo.year}
        onDisplayChange={onMonthChange}
        rangeValue={rangeValue}
        weekStartsOn={1}
        onRangeChange={handleOnRangeChange}
        getDateEnabled={getDateEnabled}>
        {isOneDate ? (
          <Month
            currentDate={now}
            viewInfo={viewInfo}
            setViewInfo={setViewInfo}
            rangeValue={rangeValue}
            onRangeValueChange={onRangeValueChange}
            lang={lang}
            isOneDate
          />
        ) : (
          <>
            <Month
              currentDate={now}
              viewInfo={viewInfo}
              setViewInfo={setViewInfo}
              rangeValue={rangeValue}
              onRangeValueChange={onRangeValueChange}
              lang={lang}
              showSimpleDay
            />
            <Month
              currentDate={now}
              viewInfo={{ ...viewInfo, month: viewInfo.month + 1 }}
              setViewInfo={setViewInfo}
              rangeValue={rangeValue}
              onRangeValueChange={onRangeValueChange}
              lang={lang}
              isNextMonth
              showSimpleDay
            />
          </>
        )}
      </CalendarBlock>
    </div>
  );
}
